/**
 * Admin › Marketing — publish to our own Instagram (@adamar_ai) and see per-post
 * analytics, without touching the terminal.
 *
 * Security model: the IG access token lives ONLY in Vercel env, behind the
 * admin-gated api/ig-* functions. The browser (1) uploads media to a public
 * Supabase bucket via a short-lived signed URL, then (2) asks the server to run
 * the Graph container -> poll -> publish flow. The token never reaches the SPA.
 */
import { useCallback, useRef, useState } from 'react';
import { supabase } from '@shared/supabase/client';
import { flushEmailQueue } from '@shared/email/flushQueue';
import './admin.css';

type MediaKind = 'REELS' | 'IMAGE' | 'CAROUSEL' | 'STORIES';

interface Insights {
  views?: number;
  reach?: number;
  saved?: number;
  shares?: number;
  total_interactions?: number;
  /** reels only, milliseconds */
  ig_reels_avg_watch_time?: number;
  ig_reels_video_view_total_time?: number;
  /** reels only, already a percentage (e.g. 63.1) */
  reels_skip_rate?: number;
}
interface MediaItem {
  id: string;
  caption?: string;
  media_type?: string;
  media_product_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  permalink?: string;
  timestamp?: string;
  like_count?: number;
  comments_count?: number;
  insights?: Insights;
}
interface Account {
  username?: string;
  followers_count?: number;
  media_count?: number;
  profile_picture_url?: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const nf = (n?: number) => (n == null ? '—' : n.toLocaleString('ru-RU'));
/** milliseconds -> "3.2с" */
const secs = (ms?: number) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)}с`);
/** Instagram's "What impacts your views" panel is just metric ÷ views. */
const rate = (n?: number, views?: number) =>
  n == null || !views ? '—' : `${((n / views) * 100).toFixed(1)}%`;
/** Colour the skip rate so a weak hook is obvious at a glance. */
const skipTone = (v?: number) => (v == null ? '' : v >= 60 ? 'mkt-bad' : v >= 45 ? 'mkt-warn' : 'mkt-good');

export default function Marketing() {
  const [kind, setKind] = useState<MediaKind>('REELS');
  const [files, setFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState('');
  const [alsoStory, setAlsoStory] = useState(true);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const [account, setAccount] = useState<Account | null>(null);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsErr, setStatsErr] = useState<string | null>(null);
  const [asRates, setAsRates] = useState(false);

  // Email test: send a real template to the admin's own inbox (admin-gated, no secret).
  const EMAIL_KINDS: { k: string; label: string }[] = [
    { k: 'finish_business', label: 'Бизнес · подключите компанию' },
    { k: 'finish_claim', label: 'Бизнес · подтвердите бренд (код)' },
    { k: 'finish_creator', label: 'Креатор · подтвердите Instagram' },
    { k: 'finish_creator_claim', label: 'Креатор · подтвердите аккаунт (код)' },
    { k: 'trial_ending', label: 'Триал заканчивается' },
    { k: 'test', label: 'Общая проверка почты' },
  ];
  const [emailKind, setEmailKind] = useState('finish_claim');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState<string | null>(null);

  // Manual broadcast to a segment (enqueues, then flushes the queue so it sends now).
  const SEGMENTS: { k: string; label: string }[] = [
    { k: 'all', label: 'Все пользователи' },
    { k: 'creators', label: 'Все креаторы' },
    { k: 'businesses', label: 'Все бизнесы' },
    { k: 'active', label: 'Только активные (оплаченные)' },
  ];
  const [bcSubject, setBcSubject] = useState('');
  const [bcBody, setBcBody] = useState('');
  const [bcSegment, setBcSegment] = useState('all');
  const [bcBusy, setBcBusy] = useState(false);
  const [bcMsg, setBcMsg] = useState<string | null>(null);

  const sendBroadcast = async () => {
    if (bcSubject.trim().length < 2 || bcBody.trim().length < 2) return;
    const segLabel = SEGMENTS.find((s) => s.k === bcSegment)?.label ?? bcSegment;
    if (!confirm(`Отправить письмо «${bcSubject.trim()}» сегменту «${segLabel}»? Отменить будет нельзя.`)) return;
    setBcBusy(true);
    setBcMsg(null);
    try {
      // Plain text → simple safe HTML (escape, paragraphs on blank lines, <br> on newlines).
      const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
      const html = bcBody.trim().split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
      const { data, error } = await supabase.rpc('admin_broadcast_email', { p_subject: bcSubject.trim(), p_html: html, p_segment: bcSegment });
      if (error) throw new Error(error.message);
      void flushEmailQueue(); // send now rather than waiting for the daily-cron sweep
      setBcMsg(`✓ Поставлено в очередь ${data ?? 0} писем — отправляются сейчас.`);
      setBcSubject('');
      setBcBody('');
    } catch (e) {
      setBcMsg(`✗ ${(e as Error).message}`);
    } finally {
      setBcBusy(false);
    }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    setEmailMsg(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const to = user?.email;
      if (!to) throw new Error('Не удалось определить ваш email');
      const q = `?test=${encodeURIComponent(to)}${emailKind && emailKind !== 'test' ? `&kind=${emailKind}` : ''}`;
      const resp = await fetch(`/api/send-emails${q}`, { headers: await authHeaders() });
      const j = (await resp.json()) as { error?: string; kind?: string; skipped?: string; result?: { ok?: boolean; error?: string } };
      if (!resp.ok || j.error) throw new Error(j.error ?? 'Ошибка отправки');
      if (j.skipped) throw new Error('SMTP не настроен на сервере (skipped)');
      if (j.result?.ok === false) throw new Error(j.result.error ?? 'SMTP отклонил письмо');
      setEmailMsg(`✓ Отправлено на ${to} (${j.kind ?? emailKind}). Проверьте инбокс.`);
    } catch (e) {
      setEmailMsg(`✗ ${(e as Error).message}`);
    } finally {
      setEmailBusy(false);
    }
  };

  const say = (m: string) => setLog((l) => [...l, m]);

  const authHeaders = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('Не авторизован');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` };
  }, []);

  const igPost = useCallback(
    async (body: Record<string, unknown>) => {
      const resp = await fetch('/api/ig-post', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(body),
      });
      const json = (await resp.json()) as { id?: string; status_code?: string; error?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Ошибка Graph API');
      return json;
    },
    [authHeaders],
  );

  // Upload one file to the public bucket via a signed URL; return its public URL.
  const uploadFile = useCallback(
    async (file: File) => {
      const resp = await fetch('/api/ig-upload-url', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ filename: file.name }),
      });
      const j = (await resp.json()) as { path?: string; token?: string; publicUrl?: string; bucket?: string; error?: string };
      if (!resp.ok || j.error || !j.token || !j.path) throw new Error(j.error ?? 'Не удалось получить upload URL');
      const { error } = await supabase.storage
        .from(j.bucket ?? 'reels-public')
        .uploadToSignedUrl(j.path, j.token, file, { contentType: file.type || undefined });
      if (error) throw new Error(`Загрузка не удалась: ${error.message}`);
      return j.publicUrl!;
    },
    [authHeaders],
  );

  // Poll a container until Instagram finishes ingesting it.
  const waitFinished = useCallback(
    async (creationId: string, label: string) => {
      for (let i = 0; i < 50; i++) {
        const { status_code } = await igPost({ action: 'status', creation_id: creationId });
        if (!status_code || status_code === 'FINISHED') return;
        if (status_code === 'ERROR' || status_code === 'EXPIRED') {
          throw new Error(`${label}: контейнер ${status_code}`);
        }
        say(`   …${label}: ${status_code}`);
        await sleep(4000);
      }
      throw new Error(`${label}: превышено время обработки`);
    },
    [igPost],
  );

  const reset = () => {
    setFiles([]);
    setCaption('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const publish = async () => {
    if (!files.length) {
      say('Сначала выбери файл(ы).');
      return;
    }
    setBusy(true);
    setLog([]);
    try {
      let publishedId = '';

      if (kind === 'REELS' || kind === 'STORIES') {
        say('Загружаю видео…');
        const url = await uploadFile(files[0]);
        say('Создаю контейнер…');
        const { id: cid } = await igPost({
          action: 'create',
          media_type: kind,
          video_url: url,
          ...(kind === 'REELS' ? { caption } : {}),
        });
        say('Instagram обрабатывает видео…');
        await waitFinished(cid!, kind);
        say('Публикую…');
        publishedId = (await igPost({ action: 'publish', creation_id: cid })).id!;

        if (kind === 'REELS' && alsoStory) {
          say('Дублирую в Stories…');
          const { id: sc } = await igPost({ action: 'create', media_type: 'STORIES', video_url: url });
          await waitFinished(sc!, 'story');
          await igPost({ action: 'publish', creation_id: sc });
          say('Stories опубликованы.');
        }
      } else if (kind === 'IMAGE') {
        say('Загружаю фото…');
        const url = await uploadFile(files[0]);
        say('Создаю контейнер…');
        const { id: cid } = await igPost({ action: 'create', image_url: url, caption });
        await waitFinished(cid!, 'photo');
        say('Публикую…');
        publishedId = (await igPost({ action: 'publish', creation_id: cid })).id!;

        if (alsoStory) {
          say('Дублирую в Stories…');
          const { id: sc } = await igPost({ action: 'create', media_type: 'STORIES', image_url: url });
          await waitFinished(sc!, 'story');
          await igPost({ action: 'publish', creation_id: sc });
          say('Stories опубликованы.');
        }
      } else {
        // CAROUSEL
        if (files.length < 2) throw new Error('Карусель — минимум 2 фото');
        if (files.length > 10) throw new Error('Карусель — максимум 10 фото');
        const childIds: string[] = [];
        for (let i = 0; i < files.length; i++) {
          say(`Загружаю фото ${i + 1}/${files.length}…`);
          const url = await uploadFile(files[i]);
          const { id } = await igPost({ action: 'create', image_url: url, is_carousel_item: true });
          await waitFinished(id!, `слайд ${i + 1}`);
          childIds.push(id!);
        }
        say('Собираю карусель…');
        const { id: parent } = await igPost({ action: 'create_carousel', children: childIds, caption });
        await waitFinished(parent!, 'карусель');
        say('Публикую…');
        publishedId = (await igPost({ action: 'publish', creation_id: parent })).id!;
      }

      say(`✓ Опубликовано. media id: ${publishedId}`);
      reset();
    } catch (e) {
      say(`✗ ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const loadStats = async () => {
    setLoadingStats(true);
    setStatsErr(null);
    try {
      const resp = await fetch('/api/ig-insights', { headers: await authHeaders() });
      const json = (await resp.json()) as { account?: Account; media?: MediaItem[]; error?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Не удалось получить аналитику');
      setAccount(json.account ?? null);
      setMedia(json.media ?? []);
    } catch (e) {
      setStatsErr((e as Error).message);
    } finally {
      setLoadingStats(false);
    }
  };

  const accept = kind === 'IMAGE' || kind === 'CAROUSEL' ? 'image/*' : 'video/*';
  const multiple = kind === 'CAROUSEL';

  return (
    <div className="mkt">
      <div className="mkt-head">
        <h2>Маркетинг · Instagram</h2>
        <p className="mkt-sub">
          Публикация на @adamar_ai и аналитика постов. Токен хранится на сервере (Vercel), в браузер не попадает.
        </p>
      </div>

      <div className="mkt-grid">
        {/* ---- Publish ---- */}
        <section className="mkt-card">
          <h3>Опубликовать</h3>

          <div className="mkt-kinds">
            {(['REELS', 'IMAGE', 'CAROUSEL', 'STORIES'] as MediaKind[]).map((k) => (
              <button
                key={k}
                className={`mkt-kind ${kind === k ? 'active' : ''}`}
                onClick={() => {
                  setKind(k);
                  setFiles([]);
                  if (fileRef.current) fileRef.current.value = '';
                }}
                disabled={busy}
              >
                {k === 'REELS' ? 'Reel' : k === 'IMAGE' ? 'Фото' : k === 'CAROUSEL' ? 'Карусель' : 'Stories'}
              </button>
            ))}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={busy}
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
          {files.length > 0 && (
            <div className="mkt-files">
              {files.map((f) => (
                <span key={f.name}>{f.name} · {(f.size / 1e6).toFixed(1)}MB</span>
              ))}
            </div>
          )}

          {kind !== 'STORIES' && (
            <textarea
              className="mkt-caption"
              placeholder="Подпись поста…"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={busy}
              rows={4}
            />
          )}

          {(kind === 'REELS' || kind === 'IMAGE') && (
            <label className="mkt-check">
              <input type="checkbox" checked={alsoStory} onChange={(e) => setAlsoStory(e.target.checked)} disabled={busy} />
              Продублировать в Stories
            </label>
          )}

          <button className="mkt-publish" onClick={publish} disabled={busy || !files.length}>
            {busy ? 'Публикую…' : 'Опубликовать'}
          </button>

          {log.length > 0 && (
            <pre className="mkt-log">{log.join('\n')}</pre>
          )}
        </section>

        {/* ---- Analytics ---- */}
        <section className="mkt-card">
          <div className="mkt-stats-head">
            <h3>Аналитика постов</h3>
            <div className="mkt-stats-actions">
              <label className="mkt-check mkt-check-inline" title="Лайки/комменты/saves/shares как % от просмотров">
                <input type="checkbox" checked={asRates} onChange={(e) => setAsRates(e.target.checked)} />
                в % от просмотров
              </label>
              <button className="mkt-refresh" onClick={loadStats} disabled={loadingStats}>
                {loadingStats ? 'Гружу…' : 'Обновить'}
              </button>
            </div>
          </div>

          {account && (
            <div className="mkt-account">
              {account.profile_picture_url && <img src={account.profile_picture_url} alt="" />}
              <div>
                <strong>@{account.username}</strong>
                <div className="mkt-account-meta">
                  {nf(account.followers_count)} подписчиков · {nf(account.media_count)} постов
                </div>
              </div>
            </div>
          )}

          {statsErr && <div className="mkt-err">{statsErr}</div>}

          {media.length > 0 && (
            <div className="mkt-table-wrap">
              <table className="mkt-table">
                <thead>
                  <tr>
                    <th>Пост</th>
                    <th>Тип</th>
                    <th>Показы</th>
                    <th>Reach</th>
                    <th title="Среднее время просмотра — насколько удержал хук">Ср. просмотр</th>
                    <th title="Доля пропусков: сколько зрителей смахнули рил. Чем ниже, тем сильнее хук">Skip rate</th>
                    <th>Лайки</th>
                    <th>Комм.</th>
                    <th>Saves</th>
                    <th>Shares</th>
                  </tr>
                </thead>
                <tbody>
                  {media.map((m) => {
                    const v = m.insights?.views;
                    const cell = (n?: number) => (asRates ? rate(n, v) : nf(n));
                    return (
                      <tr key={m.id}>
                        <td>
                          <a href={m.permalink} target="_blank" rel="noreferrer" className="mkt-post">
                            {m.thumbnail_url || m.media_url ? (
                              <img src={m.thumbnail_url ?? m.media_url} alt="" />
                            ) : (
                              <span className="mkt-noimg" />
                            )}
                            <span className="mkt-cap">{(m.caption ?? '').slice(0, 40) || '(без подписи)'}</span>
                          </a>
                        </td>
                        <td>{m.media_product_type ?? m.media_type}</td>
                        <td>{nf(v)}</td>
                        <td>{nf(m.insights?.reach)}</td>
                        <td>{secs(m.insights?.ig_reels_avg_watch_time)}</td>
                        <td className={skipTone(m.insights?.reels_skip_rate)}>
                          {m.insights?.reels_skip_rate == null ? '—' : `${m.insights.reels_skip_rate.toFixed(1)}%`}
                        </td>
                        <td>{cell(m.like_count)}</td>
                        <td>{cell(m.comments_count)}</td>
                        <td>{cell(m.insights?.saved)}</td>
                        <td>{cell(m.insights?.shares)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {media.length > 0 && (
            <p className="mkt-note">
              Skip rate и время просмотра — только для рилсов. Кривая удержания, источники просмотров
              (Reels tab / Explore / Feed) и демография по конкретному посту в Graph API недоступны —
              их отдаёт только приложение Instagram.
            </p>
          )}

          {!media.length && !loadingStats && !statsErr && (
            <p className="mkt-empty">Нажми «Обновить», чтобы подтянуть статистику постов.</p>
          )}
        </section>
      </div>

      {/* ---- Email test ---- */}
      <section className="mkt-card" style={{ marginTop: 16 }}>
        <h3>Email · тест</h3>
        <p className="mkt-sub" style={{ marginTop: 0 }}>
          Отправить реальный шаблон себе на почту (от вашей админ-сессии). Данные в письме — образцы.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={emailKind}
            onChange={(e) => setEmailKind(e.target.value)}
            disabled={emailBusy}
            style={{ padding: '8px 10px', borderRadius: 8, background: '#0f1627', color: '#dde', border: '1px solid rgba(74,158,255,0.35)', fontFamily: 'inherit', fontSize: 'var(--fs-small)' }}
          >
            {EMAIL_KINDS.map((e) => <option key={e.k} value={e.k}>{e.label}</option>)}
          </select>
          <button className="mkt-refresh" onClick={sendTestEmail} disabled={emailBusy}>
            {emailBusy ? 'Отправляю…' : 'Отправить себе'}
          </button>
        </div>
        {emailMsg && (
          <div style={{ marginTop: 10, fontSize: 'var(--fs-small)', color: emailMsg.startsWith('✓') ? '#7fd18f' : '#e6a2a2' }}>
            {emailMsg}
          </div>
        )}
      </section>

      {/* ---- Broadcast ---- */}
      <section className="mkt-card" style={{ marginTop: 16 }}>
        <h3>Рассылка</h3>
        <p className="mkt-sub" style={{ marginTop: 0 }}>
          Письмо сегменту пользователей. Обычный текст (пустая строка — новый абзац). Уходит через очередь (~15 мин), с отпиской, без дублей.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 620 }}>
          <input
            value={bcSubject}
            onChange={(e) => setBcSubject(e.target.value)}
            disabled={bcBusy}
            placeholder="Тема письма"
            style={{ padding: '9px 11px', borderRadius: 8, background: '#0f1627', color: '#dde', border: '1px solid rgba(74,158,255,0.35)', fontFamily: 'inherit', fontSize: 'var(--fs-small)' }}
          />
          <textarea
            value={bcBody}
            onChange={(e) => setBcBody(e.target.value)}
            disabled={bcBusy}
            rows={6}
            placeholder="Текст письма…"
            style={{ padding: '9px 11px', borderRadius: 8, background: '#0f1627', color: '#dde', border: '1px solid rgba(74,158,255,0.35)', fontFamily: 'inherit', fontSize: 'var(--fs-small)', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={bcSegment}
              onChange={(e) => setBcSegment(e.target.value)}
              disabled={bcBusy}
              style={{ padding: '8px 10px', borderRadius: 8, background: '#0f1627', color: '#dde', border: '1px solid rgba(74,158,255,0.35)', fontFamily: 'inherit', fontSize: 'var(--fs-small)' }}
            >
              {SEGMENTS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
            </select>
            <button className="mkt-publish" onClick={sendBroadcast} disabled={bcBusy || bcSubject.trim().length < 2 || bcBody.trim().length < 2} style={{ width: 'auto', padding: '9px 18px' }}>
              {bcBusy ? 'Ставлю в очередь…' : 'Отправить рассылку'}
            </button>
          </div>
        </div>
        {bcMsg && (
          <div style={{ marginTop: 10, fontSize: 'var(--fs-small)', color: bcMsg.startsWith('✓') ? '#7fd18f' : '#e6a2a2' }}>
            {bcMsg}
          </div>
        )}
      </section>
    </div>
  );
}
